import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, LogOut, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, gbp } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { FadeUp, PageHero } from "../components/Reveal";
import Triskelion from "../components/Triskelion";

const field = "w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

function AuthForm({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot", { email });
        setResetSent(true);
        return;
      }
      const { data } =
        mode === "login"
          ? await api.post("/auth/login", { email, password })
          : await api.post("/auth/register", { name, email, password });
      localStorage.setItem("cg_customer_token", data.token);
      onAuth(data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot") {
    return (
      <div className="max-w-md mx-auto border border-white/10 bg-ink-2/60 p-8" data-testid="forgot-form">
        <Triskelion className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={8} />
        <h2 className="font-serif text-2xl text-ivory text-center font-light mb-4">Reset your password</h2>
        {resetSent ? (
          <div className="text-center" data-testid="forgot-sent">
            <p className="text-stone-mist text-sm font-light leading-relaxed">
              If an account exists for <span className="text-ivory">{email}</span>, a reset link is on its way. It
              expires in 30 minutes.
            </p>
            <button onClick={() => { setMode("login"); setResetSent(false); }} className="mt-6 text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow" data-testid="forgot-back">
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-stone-mist text-sm font-light text-center">Enter your account email and we'll send you a reset link.</p>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} data-testid="forgot-email" />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50">
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <button type="button" onClick={() => setMode("login")} className="w-full text-stone-mist text-xs uppercase tracking-[0.2em] hover:text-gold" data-testid="forgot-cancel">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto border border-white/10 bg-ink-2/60 p-8" data-testid="account-auth">
      <Triskelion className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={8} />
      <div className="flex border-b border-white/10 mb-8">
        {["login", "register"].map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setError(""); }}
            data-testid={`auth-tab-${m}`}
            className={`flex-1 pb-3 text-xs uppercase tracking-[0.25em] transition-colors ${
              mode === m ? "text-gold border-b-2 border-gold" : "text-stone-mist hover:text-ivory"
            }`}
          >
            {m === "login" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={field} data-testid="auth-name" />
        )}
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} data-testid="auth-email" />
        <input
          required
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
          className={field}
          data-testid="auth-password"
        />
        {mode === "login" && (
          <button type="button" onClick={() => setMode("forgot")} className="text-stone-mist text-xs hover:text-gold transition-colors" data-testid="forgot-link">
            Forgot password?
          </button>
        )}
        {error && <p className="text-red-400 text-xs" data-testid="auth-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50">
          {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
        </button>
      </form>
      <p className="text-stone-mist text-xs text-center mt-6">Your orders and wishlist, all in one place.</p>
    </div>
  );
}

export default function Account() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [orders, setOrders] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const { wishlist, syncAccountWishlist } = useShop();

  useEffect(() => {
    if (!localStorage.getItem("cg_customer_token")) return setChecking(false);
    api.get("/auth/me")
      .then(({ data }) => {
        if (data.role !== "customer") throw new Error("staff");
        setUser(data);
      })
      .catch(() => localStorage.removeItem("cg_customer_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/account/orders").then(({ data }) => setOrders(data.items)).catch(() => {});
    api.get("/account/wishlist").then(({ data }) => setWishlistItems(data.items)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/account/wishlist").then(({ data }) => setWishlistItems(data.items)).catch(() => {});
  }, [wishlist, user]);

  if (checking) return <main className="pt-48 pb-32 min-h-screen" />;

  if (!user) {
    return (
      <main className="pt-40 pb-24 min-h-screen" data-testid="account-page">
        <PageHero eyebrow="Your Account" title="Sign in to Celtic Gold" sub="See your orders and keep your wishlist with you." />
        <div className="px-4 -mt-10"><AuthForm onAuth={(u) => { setUser(u); syncAccountWishlist(); }} /></div>
      </main>
    );
  }

  return (
    <main className="pt-40 pb-24 min-h-screen" data-testid="account-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <FadeUp>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
            <div>
              <h1 className="font-serif text-4xl text-ivory font-light">Hello, {user.name}</h1>
              <p className="text-stone-mist text-sm mt-1">{user.email}</p>
            </div>
            <button
              onClick={() => { localStorage.removeItem("cg_customer_token"); setUser(null); }}
              data-testid="account-logout"
              className="flex items-center gap-2 border border-white/15 text-stone-mist px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.05}>
          <h2 className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold mb-6 font-medium">
            <Package className="w-4 h-4" /> Your Orders
          </h2>
          <div className="space-y-4 mb-16" data-testid="account-orders">
            {orders.map((o) => (
              <div key={o.id} className="border border-white/10 p-5" data-testid={`account-order-${o.order_number}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-2">
                  <p className="font-mono text-gold">{o.order_number}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-mist">
                    {o.kind} · <span className={o.status === "paid" ? "text-emerald-400" : "text-gold"}>{o.status.replace(/_/g, " ")}</span>
                  </p>
                </div>
                <p className="text-stone-mist text-sm font-light">
                  {o.items.map((i) => `${i.name}${i.size ? ` (${i.size})` : ""} ×${i.qty}`).join(" · ")}
                </p>
                <p className="text-stone-mist text-xs mt-1">
                  {o.delivery_label} · Total <span className="font-mono text-gold">{gbp(o.total)}</span> ·{" "}
                  {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            ))}
            {orders.length === 0 && (
              <p className="border border-white/5 p-8 text-center text-stone-mist text-sm">
                No orders yet. <Link to="/shop/all" className="text-gold hover:text-gold-glow">Browse the shop</Link>
              </p>
            )}
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <h2 className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold mb-6 font-medium">
            <Heart className="w-4 h-4" /> Your Wishlist
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="account-wishlist">
            {wishlistItems.map((p) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="group border border-white/5 hover:border-gold/40 transition-colors" data-testid={`wishlist-item-${p.slug}`}>
                <div className="aspect-square overflow-hidden bg-ink-3">
                  {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />}
                </div>
                <div className="p-4">
                  <p className="text-ivory text-sm font-light group-hover:text-gold transition-colors">{p.name}</p>
                  <p className="font-mono text-xs text-gold mt-1">{gbp(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
          {wishlistItems.length === 0 && (
            <p className="border border-white/5 p-8 text-center text-stone-mist text-sm">Your wishlist is empty — tap the heart on any piece to save it here.</p>
          )}
        </FadeUp>
      </div>
    </main>
  );
}
